import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { useIntl } from '../../util/reactIntl';
import { parse, stringify } from '../../util/urlHelpers';
import { useSdk } from '../../sdkContext';
import { fetchProducts } from './api';

import {
  AutocompleteSelect,
  Page,
  UserNav,
  LayoutSideNavigation,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperBusinessSideNav,
  PaginationLinks,
  CategoryModal
} from '../../components';
import { TopbarContainer } from '../../containers';
import KeywordsFilter from './KeywordsFilter';
import ProductRow from './ProductRow';
import { AddItemModal } from './AddItemModal';
import css from './ManageStockPage.css';

const PAGE_SIZE = 24;

// Funkcja pomocnicza do formatowania nazw sklepów (czyszczenie "Decathlon" + prepending dkCode)
const formatStoreName = (providerAttrs = {}) => {
  const rawName = providerAttrs.secondaryName || providerAttrs.name || 'Sklep';
  const cleanedName = rawName.replace(/^Decathlon\s*/i, '').trim();
  const dkCode = providerAttrs.dkCode;

  if (dkCode) {
    return `${dkCode} - ${cleanedName || rawName}`;
  }
  return cleanedName || rawName;
};

const ManageListingsPageComponent = () => {
  const { providerId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const sdk = useSdk();
  const intl = useIntl();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [preselectedProductId, setPreselectedProductId] = useState(undefined);

  // Redux state extraction
  const user = useSelector(state => state.user);
  const currentUser = user?.currentUser;
  const currentUserProviders = user?.userProviders || [];

  // Find currently selected provider object from user's provider list
  const currentProvider = useMemo(() => {
    return currentUserProviders.find(p => (p.id?.uuid || p.id) === providerId);
  }, [currentUserProviders, providerId]);

  // Calculate permissions dynamically for the currently selected provider
  const userPermissions = useMemo(() => {
    if (!currentUser) {
      return { isAdmin: false, isOwner: false, isEmployee: false, roleLabel: 'Brak roli' };
    }

    const { isAdmin = false, adminForCountries = [] } = currentUser.attributes || {};
    const providerCountry =
      currentProvider?.attributes?.country ||
      currentProvider?.attributes?.address?.country;

    const isCountryAdmin =
      Array.isArray(adminForCountries) &&
      providerCountry &&
      adminForCountries.includes(providerCountry);

    const isEffectiveAdmin = Boolean(isAdmin || isCountryAdmin);

    if (isEffectiveAdmin) {
      return {
        isAdmin: true,
        isOwner: true,
        isEmployee: true,
        roleLabel: isAdmin ? 'Admin' : `Admin (${providerCountry?.toUpperCase()})`,
      };
    }

    const myRole = currentProvider?.attributes?.myRole;
    const isOwner = myRole === 'owner';
    const isEmployee = myRole === 'employee' || isOwner;

    return {
      isAdmin: false,
      isOwner,
      isEmployee,
      roleLabel: isOwner ? 'Właściciel (Owner)' : isEmployee ? 'Pracownik (Employee)' : 'Brak roli',
    };
  }, [currentUser, currentProvider]);

  const providersNotBanned = useMemo(() => {
    return currentUserProviders.filter(item => !item.attributes?.banned);
  }, [currentUserProviders]);

  const queryParams = parse(location.search);
  const page = parseInt(queryParams.page, 10) || 1;

  // --- ODBIERANIE WARTOŚCI FILTRÓW Z URL ---
  const managedPiecesFilter = queryParams.managedPieces || 'all'; // 'all' | 'enabled' | 'disabled'
  const stateFilter = queryParams.state || 'all'; // 'all' | 'published' | 'closed'
  const businessCategoryParam = queryParams.businessCategory;

  // Wyliczanie liczby wybranych kategorii dla odznaki na przycisku
  const selectedCategoriesCount = useMemo(() => {
    if (!businessCategoryParam) return 0;
    if (Array.isArray(businessCategoryParam)) return businessCategoryParam.length;
    return businessCategoryParam.trim().split(',').filter(Boolean).length;
  }, [businessCategoryParam]);

  const { data: response, isLoading, isError, isFetching } = useQuery({
    queryKey: ['ownListings', providerId, page, queryParams.businessCategory, queryParams.keywords],
    queryFn: () =>
      fetchProducts(sdk, {
        providerId,
        page,
        perPage: PAGE_SIZE,
        businessCategory: queryParams.businessCategory,
        keywords: queryParams.keywords,
        state: 'all',
      }),
    staleTime: 1000 * 60 * 2,
    keepPreviousData: true,
  });

  // --- FILTROWANIE PRODUKTÓW W PAMIĘCI NA PODSTAWIE FILTRÓW LOKALNYCH ---
  const rawProducts = useMemo(() => {
    return response?.data?.data || response?.data || [];
  }, [response]);

  const products = useMemo(() => {
    return rawProducts.filter(product => {
      const isManaged = product?.attributes?.managedPiecesEnabled ?? false;
      const productState = product?.attributes?.state;

      // 1. Filtr śledzenia sztuk
      if (managedPiecesFilter === 'enabled' && !isManaged) return false;
      if (managedPiecesFilter === 'disabled' && isManaged) return false;

      // 2. Filtr stanu oferty
      if (stateFilter === 'published' && productState !== 'published') return false;
      if (stateFilter === 'closed' && productState !== 'closed') return false;

      return true;
    });
  }, [rawProducts, managedPiecesFilter, stateFilter]);

  const paginationInfo = useMemo(() => {
    const meta = response?.data?.meta || response?.meta;
    if (!meta) return null;

    return {
      page,
      perPage: PAGE_SIZE,
      totalItems: meta.totalCount || 0,
      totalPages: meta.totalPages || 1,
    };
  }, [response, page]);

  const updateQueryParams = (newParams) => {
    const updated = { ...queryParams, ...newParams };

    if (updated.managedPieces === 'all') delete updated.managedPieces;
    if (updated.state === 'all') delete updated.state;

    history.push({
      pathname: location.pathname,
      search: stringify(updated),
    });
  };

  const handleProviderChange = ({ value }) => {
    if (!value) return;
    history.push(`/stock/${value}`);
  };

  const handleManagedPiecesFilterChange = (e) => {
    updateQueryParams({ managedPieces: e.target.value, page: 1 });
  };

  const handleStateFilterChange = (e) => {
    updateQueryParams({ state: e.target.value, page: 1 });
  };

  const handleCategoriesSubmit = (subcategories) => {
    const updated = {
      ...queryParams,
      businessCategory: subcategories && subcategories.length > 0 ? subcategories : undefined,
      page: 1,
    };

    if (!updated.businessCategory) {
      delete updated.businessCategory;
    }

    history.push({
      pathname: location.pathname,
      search: stringify(updated),
    });

    setCategoryModalOpen(false);
  };

  const handleOpenModal = (productId) => {
    setPreselectedProductId(productId);
    setIsModalOpen(true);
  };

  const showPagination = paginationInfo && paginationInfo.totalPages > 1;

  const productsContainerClass = `${css.productsContainer} ${
    isFetching && !isLoading ? css.fetchingMode : ''
  }`;

  return (
    <Page title="Offer Page">
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="ManageListingsPage" />
          <UserNav selectedPageName="ManageListingsPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperBusinessSideNav currentTab="ManageListingsPage" />
        <LayoutWrapperMain>

          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              backgroundColor: '#f1f5f9',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#334155',
              marginBottom: '16px',
              fontWeight: 500,
            }}
          >
            <span>Twoja rola dla wybranego sklepu:</span>
            <strong style={{ color: '#0f172a' }}>{userPermissions.roleLabel}</strong>
          </div>

          <div className={css.topHeaderRow}>
            <div className={css.providerFilterSelect}>
              <AutocompleteSelect
                inputValue={{ value: providerId }}
                onChange={handleProviderChange}
                label={intl.formatMessage({ id: 'ManageListingsPage.provider' })}
                options={providersNotBanned.map(p => ({
                  value: p.id.uuid || p.id,
                  label: formatStoreName(p.attributes || {}),
                }))}
                placeholder={intl.formatMessage({ id: 'Console.select' })}
              />
            </div>

            <button
              type="button"
              className={css.mainAddProductBtn}
              onClick={() => handleOpenModal(undefined)}
            >
              + Dodaj produkt
            </button>
          </div>

          {/* PASEK FILTRÓW */}
          <div className={css.filtersRow}>
            <KeywordsFilter location={location} history={history} />

            <div className={css.selectFiltersGroup}>
              {/* FILTR KATEGORII */}
              <div className={css.filterColumnWrapper}>
                <label className={css.filterLabel}>Kategoria:</label>
                <button
                  type="button"
                  className={css.categoryFilterBtn}
                  onClick={() => setCategoryModalOpen(true)}
                >
                  <span>Wybierz kategorie</span>
                  {selectedCategoriesCount > 0 && (
                    <span className={css.categoryBadge}>{selectedCategoriesCount}</span>
                  )}
                </button>
              </div>

              {/* FILTR ŚLEDZENIA SZTUK */}
              <div className={css.filterColumnWrapper}>
                <label htmlFor="managedPiecesFilter" className={css.filterLabel}>
                  Śledzenie sztuk:
                </label>
                <select
                  id="managedPiecesFilter"
                  value={managedPiecesFilter}
                  onChange={handleManagedPiecesFilterChange}
                  className={css.filterSelect}
                >
                  <option value="all">Wszystkie</option>
                  <option value="enabled">Włączone (Trackowane)</option>
                  <option value="disabled">Wyłączone (Nietrackowane)</option>
                </select>
              </div>

              {/* FILTR STATUSU OFERTY */}
              <div className={css.filterColumnWrapper}>
                <label htmlFor="stateFilter" className={css.filterLabel}>
                  Status oferty:
                </label>
                <select
                  id="stateFilter"
                  value={stateFilter}
                  onChange={handleStateFilterChange}
                  className={css.filterSelect}
                >
                  <option value="all">Wszystkie</option>
                  <option value="published">Otwarta</option>
                  <option value="closed">Zamknięta</option>
                </select>
              </div>
            </div>
          </div>

          <div className={css.sectionDivider} />

          {isLoading ? (
            <div className={css.loadingInsideLayout}>
              <div className={css.spinner}></div>
              <span>Ładowanie produktów...</span>
            </div>
          ) : isError ? (
            <div className={css.errorInsideLayout}>
              <span>Wystąpił błąd podczas pobierania danych. Spróbuj ponownie.</span>
            </div>
          ) : (
            <>
              <div className={productsContainerClass}>
                {products.map(product => (
                  <ProductRow
                    key={product.id.uuid || product.id}
                    product={product}
                    response={response}
                    sdk={sdk}
                    userPermissions={userPermissions}
                    currentProvider={currentProvider}
                    onAddStock={() =>
                      handleOpenModal(product.relationships?.catalogItem?.data?.id?.uuid)
                    }
                  />
                ))}
                {products.length === 0 && (
                  <div className={css.noResults}>Brak produktów spełniających wybrane kryteria.</div>
                )}
              </div>

              {showPagination && (
                <div className={css.paginationWrapper}>
                  <PaginationLinks
                    pageName="ManageStockPage"
                    pagePathParams={{ providerId }}
                    pageSearchParams={{ page }}
                    pagination={paginationInfo}
                  />
                </div>
              )}
            </>
          )}

          <AddItemModal
            open={isModalOpen}
            onOpenChange={setIsModalOpen}
            preselectedProductId={preselectedProductId}
            products={products}
            sdk={sdk}
          />

          <CategoryModal
            isOpen={categoryModalOpen}
            onCloseModal={() => setCategoryModalOpen(false)}
            onManageDisableScrolling={() => {}}
            handleSubmit={handleCategoriesSubmit}
            initBusinessCategory={
              typeof businessCategoryParam === 'string'
                ? businessCategoryParam.trim().split(',')
                : businessCategoryParam
            }
            id="CategoryModal"
          />

        </LayoutWrapperMain>
      </LayoutSideNavigation>
    </Page>
  );
};

export default ManageListingsPageComponent;